import {
  CALENDAR_MIN_SLOT_MIN,
  isoWeekdayOf,
  localDateAt,
  localMinutesAt,
  localTimeAt,
  localToInstant,
  type OccurrenceLifecycle,
  type ScheduleEntry,
} from "@edu/shared";
import * as api from "../../lib/api.ts";
import { zonesDiffer } from "../../lib/time-zone.ts";
import { dayTitle, errorText, minutesToTime, timeText, weekdayShort } from "./common.ts";

/**
 * Перенос занятия перетаскиванием: всё, что в этом жесте считается.
 *
 * Проект целиком в `docs/schedule-drag-design.md`. Здесь чистые функции, потому
 * что тестов на компоненты в проекте нет и не планируется (раздел 9 CLAUDE.md),
 * а ломается тут каждое решение молча: неверная точка хвата увозит занятие на
 * час, неверная ветка исхода рисует в одной клетке две строки, а неверная зона
 * переносит занятие туда, куда человек не показывал.
 *
 * Четыре вещи, которые здесь решаются, и каждая своим разделом проекта:
 *
 * 1. **что вообще берётся** (`draggablePiece`, раздел 3): список РАЗРЕШЁННЫХ
 *    состояний, а не запрещённых;
 * 2. **куда встанет** (`dropTarget` и `nextSnapStep`, разделы 4 и 5): минута под
 *    курсором минус хват, привязанная к сетке самого занятия шагом, который
 *    зависит от скорости руки, и настенное время СЕРИИ на выходе;
 * 3. **что с этим делать** (`dropPlan`, раздел 6): четыре исхода, и причина
 *    предыдущего переноса не стирается;
 * 4. **что сказать потом** (`dropDoneText` и `dropErrorText`, раздел 7).
 */

/**
 * Состояния, которые берутся рукой.
 *
 * ⚠️ **Список РАЗРЕШЁННЫХ, а не запрещённых, и это не стиль.**
 * `OccurrenceLifecycle` это девять значений и завтра станет десятью, и десятое
 * обязано оказаться неберущимся САМО, а не после того, как кто-то вспомнит
 * дописать запрет. Почему не берётся каждое из остальных, разобрано таблицей
 * раздела 3 проекта; короче всего так: `cancelled` и `moved_away` это заглушки,
 * `live` и `done` состоялись, а `past` это занятие группы, прошедшее ВНЕ нашей
 * комнаты, у которого нет урока, то есть серверный страж `lesson_started` про
 * него молчит.
 *
 * ⚠️ `missed` берётся, и это самый частый перенос: занятие, на которое никто не
 * пришёл, двигают чаще всего остального.
 */
const DRAGGABLE: readonly OccurrenceLifecycle[] = ["scheduled", "open", "ready", "missed"];

/**
 * Этот кусок занятия можно взять рукой.
 *
 * ⚠️ **Правило смотрит на КУСОК, а не только на строку**, и иначе оно не
 * выполнимо: `continues` (хвост занятия через полночь) это свойство куска, у
 * `ScheduleEntry` такого поля нет вовсе, а `data-entry` стоит на обеих коробках.
 * Взяв занятие за хвост, человек получил бы «положил на 15:00, встало на 13:30»:
 * точка взятия не совпадает с началом занятия.
 */
export function draggablePiece(entry: ScheduleEntry, piece: { continues: boolean }): boolean {
  if (entry.myRole !== "teacher") return false;
  if (piece.continues) return false;
  if (entry.state === null || !DRAGGABLE.includes(entry.state)) return false;
  // Разовое занятие двигается сменой `scheduled_at` у самого урока, поэтому без
  // урока адресовать его нечем
  if (entry.standalone) return entry.lessonId !== null;
  // Осиротевший урок (`source: "lesson"` без `standalone`) сюда не попадает:
  // ручка разового занятия отвергает урок со слотом
  return entry.source === "series" && entry.slotId !== null && entry.occursOn !== null;
}

/**
 * Зона, в которой ручка примет дату и время.
 *
 * У занятия серии это зона СЕРИИ: хранится настенное время
 * (`schedule_exception.moved_to_date`, `moved_to_time`) плюс имя зоны. У разового
 * занятия серии нет вовсе, и зона это зона смотрящего, ровно как в его форме
 * переноса (`SingleEntryTools`).
 */
export function wallZone(entry: ScheduleEntry, viewerZone: string): string {
  return entry.seriesTimeZone ?? viewerZone;
}

/**
 * За сколько минут от начала занятия взяли коробку.
 *
 * ⚠️ **Без этой поправки жест врёт на целый час.** Коробка занятия высотой в
 * свою длительность: полтора часа это три клетки. Взяв занятие за нижнюю треть и
 * отпустив ровно там, где оно стояло, человек попал бы указателем в третью
 * клетку и уехал бы на час. Хват запоминается на взятии и вычитается из минуты
 * под указателем, и занятие едет за курсором целиком, как в любом календаре.
 *
 * Считается ДОЛЕЙ высоты, а не пикселями на минуту: у коробки в стилях снята
 * пара пикселей на зазор (`- 2px`), и деление на «пиксели минуты» промахивалось
 * бы тем сильнее, чем длиннее занятие.
 *
 * ⚠️ **В минутах, а не в получасовых ступенях, с 10.09.2026.** Шаг привязки стал
 * зависеть от скорости руки (`nextSnapStep`), и хват, округлённый до получаса,
 * при шаге в пять минут давал бы прыжок на четверть часа в момент взятия.
 */
export function grabMinutes(offsetY: number, boxHeight: number, drawnMin: number): number {
  if (boxHeight <= 0) return 0;
  const share = Math.min(Math.max(offsetY / boxHeight, 0), 1);
  return share * drawnMin;
}

/**
 * Шаг привязки: насколько крупными ступенями занятие встаёт под курсором.
 *
 * ⚠️ **Шаг зависит от СКОРОСТИ руки** (просьба владельца 10.09.2026): ведёшь
 * быстро, значит выбираешь час и день, и получас это то, чем время называют;
 * замедлился, значит доводишь, и нужны пятнадцать минут, а потом пять. Так
 * привычная крупная сетка не мешает точной постановке, а точная не мешает
 * быстрому переносу через неделю.
 *
 * ⚠️ **Грубее шаг становится неохотнее, чем точнее** (порог с запасом в 1,4):
 * без этого дрожь руки на медленном движении перекидывала бы шаг туда и обратно,
 * и занятие прыгало бы под курсором на четверть часа.
 *
 * ⚠️ **У шага есть ПОЛ, и у пальца он крупнее** (12.09.2026): час рисуется
 * высотой `--cal-hour: 3.4rem`, то есть пиксель это примерно 1,1 минуты, а
 * точность пальца около пяти пикселей. Пятиминутный шаг там мельче самого
 * указателя, и человек видел бы «целился в 15:00, получил 15:05». Мышь передаёт
 * пол 5, то есть ведёт себя ровно как раньше, палец 15.
 */
export type SnapStep = 30 | 15 | 5;

/** Медленнее этого считаем, что человек доводит занятие. Пиксели в секунду. */
const FINE_PX_S = 110;
/** Быстрее этого человек переносит, а не доводит. */
const COARSE_PX_S = 380;
/** Запас, с которым шаг возвращается к грубому. */
const HYSTERESIS = 1.4;

/** Шаг по одной скорости, без пола: пол накладывается сверху. */
function bySpeed(current: SnapStep, pxPerSec: number): SnapStep {
  if (current === 5) {
    if (pxPerSec > COARSE_PX_S * HYSTERESIS) return 30;
    if (pxPerSec > FINE_PX_S * HYSTERESIS) return 15;
    return 5;
  }
  if (current === 15) {
    if (pxPerSec > COARSE_PX_S * HYSTERESIS) return 30;
    if (pxPerSec < FINE_PX_S) return 5;
    return 15;
  }
  if (pxPerSec < FINE_PX_S) return 5;
  if (pxPerSec < COARSE_PX_S) return 15;
  return 30;
}

export function nextSnapStep(current: SnapStep, pxPerSec: number, floor: SnapStep = 5): SnapStep {
  const next = bySpeed(current, pxPerSec);
  return next < floor ? floor : next;
}

/** Нарисованная высота занятия в минутах: та же, по которой стоит коробка. */
export function drawnMinutes(entry: ScheduleEntry): number {
  return Math.max(entry.durationMin ?? CALENDAR_MIN_SLOT_MIN, CALENDAR_MIN_SLOT_MIN);
}

export interface DropTarget {
  /** Колонка, на которую положили. Дата в зоне СМОТРЯЩЕГО. */
  viewerDate: string;
  /** Минуты от местной полуночи смотрящего: из них рисуется призрак и подпись. */
  viewerMin: number;
  /** Настенная дата в зоне, которую примет ручка (`wallZone`). */
  date: string;
  /** Настенное время там же, `ЧЧ:ММ`. */
  time: string;
}

/**
 * Куда встанет занятие, если отпустить его над этой клеткой.
 *
 * ⚠️ **Клиент шлёт настенное время СЕРИИ, ровно то же, что набирают в форме.**
 * В первой редакции проекта здесь предлагалось необязательное поле `timeZone` в
 * теле запроса («клиент шлёт свою зону, сервер переводит»), и предложение снято:
 * хранение настенное, чтение обратное, а политика двойного часа это «берём
 * РАННЕЕ из двух», то есть круг момент → настенное время → момент не
 * тождественен. Поздняя половина осеннего двойного часа в зоне серии не
 * выражается вовсе, а весенний несуществующий час молча стал бы обычным, потеряв
 * флаг. Разбор целиком в разделе 5 проекта.
 *
 * ⚠️ **Когда зоны совпадают, арифметики нет вовсе**, и сравнение идёт ПО
 * ПОВЕДЕНИЮ (`zonesDiffer`), а не по строке: `Europe/Kyiv` и `Europe/Kiev` это
 * одна зона под разными версиями ICU.
 *
 * ⚠️ **Привязка идёт по сетке САМОГО ЗАНЯТИЯ, а не по круглым часам.** Ступени
 * отсчитываются от его собственной минуты: серия на 15:20 при шаге в полчаса
 * встаёт на :20 и :50, при шаге в пятнадцать минут на :05, :20, :35 и :50, при
 * пяти минутах на любую пятиминутку. Отсюда два свойства, и оба нужны. Занятие
 * на :20 нельзя сдвинуть случайно на :00, то есть расписание на нечётных минутах
 * двигается мышью, а не ломается ею. И замедление руки НЕ двигает занятие само
 * по себе: набор мест, куда оно может встать, только пополняется.
 *
 * Минута берётся в зоне СМОТРЯЩЕГО, потому что призрак и подпись нарисованы в
 * его сетке; настенной минуте серии это ничего не стоит, разница зон постоянна и
 * при переводе обратно сокращается.
 */
export function dropTarget(
  entry: ScheduleEntry,
  viewerZone: string,
  /** Дата колонки и НЕПРИВЯЗАННАЯ минута начала: под курсором минус хват. */
  at: { date: string; startMin: number },
  snap: SnapStep,
): DropTarget {
  const phase = ((localMinutesAt(entry.at, viewerZone) % snap) + snap) % snap;
  /*
    Отрицательного начала суток не бывает, поэтому хват упирается в полночь
    колонки. Достижимо это только на неделе, где диапазон часов уже расширен до
    00:00 самим занятием, и «положил в первую строку суток» честнее, чем перескок
    в предыдущий день, которого человек не видел.
  */
  const snapped = Math.round((at.startMin - phase) / snap) * snap + phase;
  // Упор в полночь колонки поднимает занятие на ближайшую СВОЮ ступень, а не на
  // ноль: занятие на :20 обязано остаться на :20 и в первой строке суток
  const viewerMin = snapped >= 0 ? snapped : ((snapped % snap) + snap) % snap;
  const zone = wallZone(entry, viewerZone);
  const time = minutesToTime(viewerMin);
  if (!zonesDiffer(zone, viewerZone)) {
    return { viewerDate: at.date, viewerMin, date: at.date, time };
  }
  const instant = localToInstant(at.date, time, viewerZone).at;
  return {
    viewerDate: at.date,
    viewerMin,
    date: localDateAt(instant, zone),
    time: localTimeAt(instant, zone),
  };
}

/**
 * Что делает отпускание.
 *
 * `none` это «цель совпала с текущим местом»: на сервер не ходим вовсе.
 * `clear` это СНЯТИЕ исключения, а не перенос назад (см. `dropPlan`).
 * `move` это перенос той ручкой, которая полагается виду строки.
 */
export type DropAction =
  | { kind: "none" }
  | { kind: "clear" }
  | { kind: "move"; date: string; time: string; reason: string | null };

export interface DropPlan {
  target: DropTarget;
  action: DropAction;
  /**
   * Чем вернуть ровно то, что было до жеста: прежнее место, прежний вид
   * исключения, прежняя причина.
   *
   * ⚠️ **Считается ДО запроса, из строки, которую человек взял рукой.** После
   * переноса прежнего места в ответе нет: `movedFrom` укажет на естественное
   * место занятия, а оно и есть то, откуда занятие уехало ещё в прошлый раз.
   */
  undo: DropAction;
}

/**
 * Четыре исхода отпускания над клеткой (раздел 6 проекта).
 *
 * ⚠️ **Исход 2 это снятие исключения, а не перенос назад.** Цель, совпавшая с
 * ЕСТЕСТВЕННЫМ местом перенесённого занятия, переносом записала бы «перенесено
 * на четверг 15:00», лежащее на четверге 15:00, и сетка нарисовала бы в одной
 * клетке ДВЕ строки: полоску-заглушку и само занятие. Заглушка и перенесённое
 * занятие производятся двумя разными проходами раскрытия и различаются только
 * суффиксом ключа.
 *
 * ⚠️ Сравнение идёт СТРОКАМИ в зоне серии, а не моментами: сравнивается ровно то
 * значение, которое мы собираемся отправить, с `movedFrom`, который сервер
 * прислал в той же зоне. Так ветка не может разойтись с отправкой, даже если
 * база часовых поясов в браузере однажды разойдётся с серверной.
 *
 * ⚠️ **Причина предыдущего переноса не стирается.** Повторный перенос это
 * `UPDATE` той же строки исключения, и `reason: null` затёр бы фразу «болезнь»,
 * которую ученик уже прочитал в карточке. Жест шлёт обратно `entry.reason`: он
 * не трогает того, о чём не спрашивал.
 *
 * ⚠️ Исход 4 (мёртвая зона, окно браузера, Escape) сюда не приходит вовсе: там
 * `drop` не случается, и снимает жест `dragend`.
 *
 * ⚠️ **Известный предел исхода 2.** Минуту цель берёт у НЫНЕШНЕГО места занятия
 * (раздел 4), а сравнивается она с естественным местом, поэтому занятие, у
 * которого эти минуты разошлись (серия на :00, а перенесли его руками на :20),
 * в свою старую клетку целью не попадает и получает обычный перенос на :20.
 * Достижимо это только через форму с набранной вручную минутой, итог виден
 * словами, и снимается он кнопкой «Вернуть». Закрыто тестом, чтобы предел был
 * известен, а не найден.
 *
 * ⚠️ У разового занятия исходов ТРИ: исключений у него нет, его естественное
 * место это то, где он стоит.
 */
export function dropPlan(
  entry: ScheduleEntry,
  viewerZone: string,
  at: { date: string; startMin: number },
  snap: SnapStep,
): DropPlan {
  const target = dropTarget(entry, viewerZone, at, snap);
  const zone = wallZone(entry, viewerZone);
  const here = { date: localDateAt(entry.at, zone), time: localTimeAt(entry.at, zone) };

  /*
    Прежнее состояние строки одной веткой: занятие либо стояло на своём месте
    без исключения (тогда откат это снятие), либо уже было сдвинуто (тогда откат
    это перенос на то место, где оно стояло минуту назад, а НЕ на `movedFrom`:
    `movedFrom` это естественное место, с которого его увезли в прошлый раз).
  */
  const undo: DropAction =
    entry.movedFrom === null && !entry.standalone
      ? { kind: "clear" }
      : { kind: "move", date: here.date, time: here.time, reason: entry.reason };

  if (target.date === here.date && target.time === here.time) {
    return { target, action: { kind: "none" }, undo: { kind: "none" } };
  }
  if (
    entry.movedFrom !== null &&
    target.date === entry.movedFrom.date &&
    target.time === entry.movedFrom.time
  ) {
    return { target, action: { kind: "clear" }, undo };
  }
  return {
    target,
    action: { kind: "move", date: target.date, time: target.time, reason: entry.reason },
    undo,
  };
}

/**
 * Подпись внутри призрака: «чт, 15:00».
 *
 * День дублирует шапку колонки намеренно: колонка узкая, а глаз в этот момент на
 * курсоре, и одно слово тут дешевле, чем промах. Времени хватает своего: это
 * зона смотрящего, та же, в которой нарисована вся сетка.
 */
export function ghostLabel(target: DropTarget): string {
  return `${weekdayShort(isoWeekdayOf(target.viewerDate))}, ${minutesToTime(target.viewerMin)}`;
}

/**
 * Итог словами СЕРВЕРА: где занятие оказалось после перечитывания недели.
 *
 * ⚠️ Строка называет то, что вернул сервер, а не то, что мы просили. Двойной час
 * и весенний разрыв существуют в продукте и без жеста, и прятать их жест не
 * имеет права: пометку о них показывает отдельная строка окна (`markNote`).
 */
export function dropDoneText(entry: ScheduleEntry | null, zone: string, action: DropAction): string {
  const back = action.kind === "clear";
  if (entry === null) return back ? "Занятие вернулось на своё место." : "Занятие переехало.";
  const when = `${dayTitle(localDateAt(entry.at, zone))}, ${timeText(entry.at, zone)}`;
  return back ? `Занятие вернулось на своё место: ${when}.` : `Занятие переехало: ${when}.`;
}

/**
 * Отказ словами, в редакции ЖЕСТА.
 *
 * ⚠️ Второго словаря тут нет: код остаётся один, разной становится формулировка
 * на разной поверхности. Фраза формы кончается словами «Выберите другое», а
 * выбирать после отпускания не в чем: окно стоит у места падения, полей в нём
 * нет, и совет вести не к чему.
 *
 * ⚠️ Своя редакция ровно одна, хотя раздел 7 проекта называл две. Вторая
 * (`move_too_far`) на момент работы уже ничего не советовала, и переписывать её
 * значило бы завести расхождение там, где его нет.
 */
const DRAG_ERRORS: Readonly<Partial<Record<api.ApiErrorCode, string>>> = {
  occurrence_taken: "На это время у серии уже стоит занятие.",
};

export function dropErrorText(e: unknown): string {
  if (e instanceof api.ApiError) {
    const own = DRAG_ERRORS[e.code];
    if (own !== undefined) return own;
  }
  return errorText(e);
}
